<template>
	<app-header-teleport>
		<div class="flex flex-1 items-center">
			<app-title title="Finance" />
			<div class="ml-2">
				<app-advanced-filter @onFilter="onFilter" @onClear="onClear" :loading="isLoading">
					<app-advanced-date-filter v-model="filters" :start-date="'startDate'" :end-date="'endDate'" />
				</app-advanced-filter>
			</div>
		</div>
	</app-header-teleport>
	<div class="flex flex-col md:flex-row">
		<app-group-card-filter
			container-class="flex flex-col"
			v-model="filters.paymentStatus"
			:filters="[...paymentStatistics].slice(0, 3)"
			:statistics="statistics"
		/>
		<app-group-card-filter
			container-class="flex flex-col"
			v-model="filters.paymentStatus"
			:filters="[...paymentStatistics].slice(3)"
			:statistics="statistics"
		/>
		<div class="flex flex-1">
			<app-section class="mt-4 flex-1">
				<app-bar-chart
					name="Finance"
					:labels="chartData.labels"
					:series="chartData.series"
					:currency="statistics.Currency"
				/>
			</app-section>
		</div>
	</div>
	<app-section class="tight mt-4">
		<div class="flex">
			<div
				class="flex px-5 py-1 cursor-pointer border-b-2 "
				:class="filters.localStatus === LOCAL_ALL ? 'border-primary' : 'border-border-color'"
				@click="filters.localStatus = LOCAL_ALL"
			>
				{{ $t('all') }}
			</div>
			<div
				class="flex px-5 py-1 cursor-pointer border-b-2 "
				:class="filters.localStatus === LOCAL_PAID ? 'border-primary' : 'border-border-color'"
				@click="filters.localStatus = LOCAL_PAID"
			>
				{{ $t('paid') }}
			</div>
			<div
				class="flex px-5 py-1 cursor-pointer border-b-2 "
				:class="filters.localStatus === LOCAL_UNPAID ? 'border-primary' : 'border-border-color'"
				@click="filters.localStatus = LOCAL_UNPAID"
			>
				{{ $t('unpaid') }}
			</div>
			<div
				class="flex px-5 py-1 cursor-pointer border-b-2 "
				:class="filters.localStatus === LOCAL_PAYMENTS ? 'border-primary' : 'border-border-color'"
				@click="$router.push({ name: RouteNames.Payments })"
			>
				{{ $t('financePayments') }}
			</div>
		</div>
		<app-table :columns="columns" :data="invoices" :expandable="true" :one-at-a-time="true" @rowClick="onRowClick">
			<template v-slot:id="{ item }">
				<app-details-shortcut :route-name="RouteNames.InvoiceDetails" :identifier="item.id" />
				<span
					class="text-white text-10 p-px rounded-md ml-1"
					:class="item['officialNo'] === '' ? 'bg-primary' : 'bg-success'"
					v-if="isNew(item) || item['officialNo'] !== ''"
				>
					{{ item['officialNo'] === '' ? 'NEW' : 'SENT' }}
				</span>
			</template>
			<template v-slot:status="{ item }">
				<span
					class="text-white text-10 px-1 py-px rounded-md ml-1"
					:class="isPaid(item) ? 'bg-success' : 'bg-primary'"
				>
					{{ isPaid(item) ? 'Paid' : 'Unpaid' }}
				</span>
			</template>
			<template v-slot:restaurantName="{ item }">
				{{ item.restaurant['restaurantName'] }}
			</template>
			restaurantTotalPercentage
			<template v-slot:restaurantTotalPercentage="{ item }">
				{{ Number.parseFloat(item.restaurantTotalPercentage).toFixed(2) }}
			</template>
			<template v-slot:officialNo="{ item }">
				<span v-if="item['officialNo']">{{ item['officialNo'] }}</span>
				<span v-else>n/d</span>
			</template>
			<template v-slot:officialDate="{ item }">
				<span v-if="item['officialNo']">{{ $filters.formatShortDate(item['officialDate']) }}</span>
				<span v-else>n/d</span>
			</template>
			<template v-slot:sales="{ item }">
				<span>{{ item['accManager'].name }} {{ item['accManager'].surname }}</span>
			</template>
			<template v-slot:subtotal="{ item }">{{
				$filters.formatCurrency(item['subtotal'], item['restaurant'].currency)
			}}</template>
			<template v-slot:vat="{ item }">{{
				$filters.formatCurrency(item['vat'], item['restaurant'].currency)
			}}</template>
			<template v-slot:total="{ item }">{{
				$filters.formatCurrency(item['total'], item['restaurant'].currency)
			}}</template>
			<template v-slot:paid="{ item }">{{
				$filters.formatCurrency(item['paid'], item['restaurant'].currency)
			}}</template>
			<template v-slot:unpaid="{ item }">{{
				$filters.formatCurrency(item['unpaid'], item['restaurant'].currency)
			}}</template>
			<template v-slot:appDetails="{ item }">
				<div class="flex flex-1 items-center flex-col" v-if="item.paid === 0">
					<div class="py-5" v-if="!selectedInvoice">
						<p class="my-2">No payments for this invoice.</p>
					</div>
				</div>
				<div class="flex flex-1" v-else>
					<div class="flex flex-col pr-4 py-2 border-r-1 border-dashed border-border-color">
						<span class="text-13">Details</span>
					</div>
					<div class="flex flex-1 flex-col">
						<div class="flex flex-1">
							<div
								class="flex flex-col px-4 border-r-1 border-dashed border-border-color"
								v-if="invoicePayments.length > 0"
							>
								<span class="font-bold my-2">PAYMENTS</span>
								<span class="py-1" v-for="(p, index) in invoicePayments" :key="p.id">
									#Payment{{ index + 1 }} {{ $filters.formatShortDate(p.docDate) }} -
									{{ $filters.formatCurrency(p.amountCents / 100, item['restaurant'].currency) }}
								</span>
							</div>
							<div class="flex flex-col px-4 border-r-1 border-dashed border-border-color">
								<p class="py-1">
									<span class="font-bold">NUMBER: </span>
									<span>#{{ item.officialNo }}</span>
								</p>
								<p class="py-1">
									<span class="font-bold">INVOICE DATE: </span>
									<span>{{ $filters.formatShortDate(item.docDate) }}</span>
								</p>
								<p class="py-1">
									<span class="font-bold">DUE DATE: </span>
									<span>{{ $filters.formatShortDate(item.dueDate) }}</span>
								</p>
							</div>
							<div class="flex flex-col px-4 border-r-1 border-dashed border-border-color">
								<p class="py-1">
									<span class="font-bold">ACCOUNT MANAGER: </span>
									<span>{{ item.accManager.name }} {{ item.accManager.surname }}</span>
								</p>
							</div>
							<div class="flex flex-1 flex-col px-4">
								<p class="py-1">
									<span class="font-bold">NOTES: </span>
									<span>{{ item.description ? item.description : 'n/d' }}</span>
								</p>
							</div>
						</div>
					</div>
					<div class="flex flex-col pl-4 py-2 border-l-1 border-dashed border-border-color">
						<app-details-shortcut :route-name="RouteNames.InvoiceDetails" :identifier="item.id">
							<span class="text-info text-right">View Details</span>
						</app-details-shortcut>
						<div class="py-2">
							<p class="font-bold">PAID</p>
							<p class="text-success text-xl">
								{{
									$filters.formatCurrency(item['paidAmountCents'] / 100, item['restaurant'].currency)
								}}
							</p>
						</div>
						<div class="py-2">
							<p class="font-bold">UNPAID</p>
							<p class="text-warning text-xl">
								{{
									$filters.formatCurrency(
										item['unPaidAmountCents'] / 100,
										item['restaurant'].currency
									)
								}}
							</p>
						</div>
					</div>
				</div>
			</template>
		</app-table>
		<app-pagination
			:filters="filters"
			@prevPage="prevPage"
			@nextPage="nextPage"
			@limitChange="changeLimit"
			:loading="isLoading"
		/>
	</app-section>
</template>

<script>
	import RouteNames from '@/router/names';
	import AppTitle from '@/components/app/AppTitle';
	import AppAdvancedFilter from '@/components/app/AppAdvancedFilter';
	import AppAdvancedDateFilter from '@/components/app/AppAdvancedDateFilter';
	import AppTable from '@/components/app/AppTable';
	import AppPagination from '@/components/app/AppPagination';
	import AppGroupCardFilter from '@/components/app/AppGroupCardFilter';
	import AppBarChart from '@/components/app/AppBarChart';
	import constants from '@/constants/finance';
	import FinanceService from '@/services/FinanceService';
	import moment from 'moment';
	import { calcProgressPercentage } from '@/services/UtilityService';
	import AppDetailsShortcut from '@/components/app/AppDetailsShortcut';
	import { mapGetters } from 'vuex';

	const LOCAL_ALL = '';
	const LOCAL_PAID = 'LOCAL_PAID';
	const LOCAL_UNPAID = 'LOCAL_UNPAID';
	const LOCAL_PAYMENTS = 'LOCAL_PAYMENTS';

	export default {
		name: 'CreateOrder',
		components: {
			AppDetailsShortcut,
			AppTitle,
			AppAdvancedFilter,
			AppAdvancedDateFilter,
			AppTable,
			AppPagination,
			AppGroupCardFilter,
			AppBarChart,
		},
		computed: {
			page() {
				return this.$route.params;
			},
			isLoading() {
				return this.loadingStatistics || this.loadingChart || this.loadingInvoices;
			},
			invoices() {
				if (!this.filters.localStatus) {
					return this.allInvoices;
				}
				return this.allInvoices.filter((i) =>
					this.filters.localStatus === LOCAL_PAID ? this.isPaid(i) : !this.isPaid(i)
				);
			},
			...mapGetters({
				selectedRestaurants: 'selectedRestaurants',
				user: 'user',
			}),
			isManagingOneBusiness() {
				return this.user?.restaurants?.length === 1;
			},
		},
		data: () => ({
			RouteNames,
			LOCAL_ALL,
			LOCAL_PAID,
			LOCAL_UNPAID,
			LOCAL_PAYMENTS,
			visibleInvoice: null,
			selectedInvoice: null,
			paymentStatistics: constants.paymentStatistics,
			loadingInvoices: false,
			loadingStatistics: false,
			loadingChart: false,
			exporting: false,
			filters: { ...constants.defaultFilters },
			searchFilters: { ...constants.defaultFilters },
			columns: constants.columns,
			allInvoices: [],
			invoicePayments: [],
			statistics: {},
			chartData: {
				labels: [],
				series: [],
			},
		}),
		methods: {
			isNew(invoice) {
				return moment()
					.add(-30, 'days')
					.isBefore(moment(invoice['docDate']));
			},
			isPaid(invoice) {
				return invoice && invoice['unPaidAmountCents'] <= invoice['paidAmountCents'];
			},
			getInvoices() {
				this.loadingInvoices = true;
				FinanceService.getInvoices({
					...this.searchFilters,
					businessIDs: this.selectedRestaurants.join('-'),
				})
					.then((invoices) => {
						this.allInvoices = invoices;
					})
					.finally(() => {
						this.emitter.emit('isLoading', false);
						this.loadingInvoices = false;
					});
			},
			getInvoicePayments() {
				if (this.visibleInvoice) {
					FinanceService.getInvoicePayments(this.visibleInvoice.id).then((payments) => {
						this.invoicePayments = payments;
					});
				} else {
					this.invoicePayments = [];
				}
			},
			getDashboardInfo() {
				this.loadingChart = true;
				this.loadingStatistics = true;
				let payload = {
					startDate: this.searchFilters.startDate,
					endDate: this.searchFilters.endDate,
					businessIDs: this.selectedRestaurants.join('-'),
				};
				if (this.isManagingOneBusiness) {
					payload.cityId = this.user.restaurants[0]?.city?.id;
				} else {
					payload.cityId = this.user.restaurants.find(
						(r) => r.restaurantID === this.selectedRestaurants[0]
					)?.city?.id;
				}
				FinanceService.getDashboardInfo(payload)
					.then(({ chart: chartData, statistics }) => {
						let paid = chartData.map((i) => Math.floor(parseInt(i['PaymentsPerDate'])));
						let unpaid = chartData.map((i) => -Math.floor(parseInt(i['AmountWithVatPerDate'])));
						this.chartData = {
							labels: chartData.map((i) => i.Date),
							series: [
								{
									name: this.$t('paid'),
									color: '#0eb746',
									data: paid,
								},
								{
									name: this.$t('unpaid'),
									color: '#ff1149',
									data: unpaid,
								},
							],
						};
						this.statistics = statistics;
						this.setTextValues();
					})
					.finally(() => {
						this.loadingChart = false;
						this.loadingStatistics = false;
					});
			},
			calcProgressPercentage,
			setTextValues() {
				this.paymentStatistics.map((filter) => {
					filter.text = this.$filters.formatCurrency(
						this.statistics[filter.propKey],
						this.statistics.Currency
					);
					filter.comparison = this.calcProgressPercentage(
						this.statistics[filter.propKey],
						this.statistics.compared[filter.propKey]
					);
				});
			},
			onFilter() {
				this.searchFilters = { ...this.filters };
				this.refresh();
			},
			onClear() {
				this.filters = { ...constants.defaultFilters };
				this.searchFilters = { ...constants.defaultFilters };
				this.refresh();
			},
			refresh() {
				this.$router.push({ query: this.searchFilters });
				this.getInvoices();
				this.getInvoicePayments();
				this.getDashboardInfo();
			},
			parseIntQueryParams() {
				this.searchFilters.offset = parseInt(this.searchFilters.offset);
				this.searchFilters.limit = parseInt(this.searchFilters.limit);
			},
			prevPage() {
				this.parseIntQueryParams();
				this.searchFilters.offset = Math.max(this.searchFilters.offset - this.searchFilters.limit, 0);
				this.getInvoices();
			},
			nextPage() {
				this.parseIntQueryParams();
				this.searchFilters.offset = this.searchFilters.offset + this.searchFilters.limit;
				this.getInvoices();
			},
			changeLimit(value) {
				this.parseIntQueryParams();
				this.searchFilters = {
					...this.searchFilters,
					limit: value,
					offset: 0,
				};
				this.getInvoices();
			},
			createInvoice() {
				this.$router.push({ name: RouteNames.InvoiceDetails, params: { id: 'new' } });
			},
			onRowClick(invoice) {
				this.selectedInvoice = null;
				this.visibleInvoice = this.visibleInvoice ? null : invoice;
				this.getInvoicePayments();
			},
			updateFilter(key, val) {
				this.filters[key] = val;
				this.searchFilters[key] = val;
				this.refresh();
			},
			initPage() {
				if (Object.keys(this.$route.query).length > 0) {
					let filters = Object.assign(constants.defaultFilters, this.$route.query);
					this.filters = filters;
					this.searchFilters = filters;
				}
				this.parseIntQueryParams();
				this.refresh();
			},
		},
		watch: {
			page() {
				this.initPage();
			},
			selectedRestaurants() {
				if (!this.$store.getters.token) return;
				this.refresh();
			},
		},
		mounted() {
			this.emitter.emit('isLoading', true);
			this.initPage();
		},
	};
</script>

<style lang="scss" scoped>
	:deep(.btn.ladda-button.force.small) {
		padding: 3px !important;
		min-width: 0 !important;
		font-size: 13px;
	}
	:deep(.restaurant-autocomplete.app-input-container) {
		@apply mt-6;
	}
</style>
